import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import io from "socket.io-client";
import DeckGL from "@deck.gl/react";
import Map from "react-map-gl/maplibre";
import { ScatterplotLayer, TextLayer } from "@deck.gl/layers";
import "maplibre-gl/dist/maplibre-gl.css";

const SOCKET_URL = process.env.REACT_APP_API_URL || window.location.origin;
const MAP_STYLE  = "https://api.maptiler.com/maps/base-v4/style.json?key=qjoXX9L2A2sutJRBuk7t";

const TEAMS = {
  port_royale: { label: "Port Royale", color: "#92400e", bg: "#fffbeb", border: "#f59e0b", light: "#fef3c7" },
  tortuga:     { label: "Tortuga",     color: "#1e3a8a", bg: "#eff6ff", border: "#60a5fa", light: "#dbeafe" },
};

const OPTION_LABELS = ["A", "B", "C", "D"];

// ─────────────────────────────────────────────────────────────
// QuizMap — marker always visible, label only after reveal
// ─────────────────────────────────────────────────────────────

function QuizMap({ viewport, marker, markerLabel, revealed }) {
  const [view, setView] = useState({
    ...viewport, pitch: 0, bearing: 0,
    minZoom: viewport.zoom - 1, maxZoom: viewport.zoom + 4,
  });

  useEffect(() => {
    setView({
      ...viewport, pitch: 0, bearing: 0,
      minZoom: viewport.zoom - 1, maxZoom: viewport.zoom + 4,
      transitionDuration: 800,
    });
  }, [viewport.longitude, viewport.latitude, viewport.zoom]);

  const layers = useMemo(() => {
    if (!marker) return [];

    // Dot always visible
    const dotLayer = new ScatterplotLayer({
      id: "quiz-marker",
      data: [marker],
      getPosition: d => [d.longitude, d.latitude],
      getFillColor: [220, 38, 38, 230],
      getRadius: 5,
      radiusUnits: "pixels",
      radiusMinPixels: 10,
      radiusMaxPixels: 30,
      stroked: true,
      getLineColor: [255, 255, 255, 255],
      lineWidthMinPixels: 2,
    });

    // Label only after reveal
    if (!revealed || !markerLabel) return [dotLayer];

    const labelLayer = new TextLayer({
      id: "quiz-marker-label",
      data: [{ position: [marker.longitude, marker.latitude + 0.8], text: markerLabel }],
      getPosition: d => d.position,
      getText: d => d.text,
      getSize: 14,
      getColor: [30, 30, 30, 255],
      getBackgroundColor: [255, 255, 255, 220],
      background: true,
      backgroundPadding: [6, 3, 6, 3],
      getBorderColor: [200, 200, 200, 255],
      getBorderWidth: 1,
      fontWeight: "bold",
      getAnchor: "middle",
      getAlignmentBaseline: "bottom",
    });

    return [dotLayer, labelLayer];
  }, [marker, markerLabel, revealed]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%", overflow: "hidden" }}>
      <DeckGL
        viewState={view}
        onViewStateChange={({ viewState }) => setView(viewState)}
        controller={true}
        layers={layers}
        style={{ position: "absolute", inset: 0 }}
        useDevicePixels={true}
      >
        <Map
          mapStyle={MAP_STYLE}
          onLoad={(e) => {
            const map = e.target;
            map.getStyle().layers.forEach((layer) => {
              if (layer.type === "symbol")
                map.setLayoutProperty(layer.id, "visibility", "none");
            });
          }}
        />
      </DeckGL>
      {!revealed && (
        <div style={{
          position: "absolute", bottom: 8, left: 8,
          background: "rgba(0,0,0,0.55)", color: "#fff",
          fontSize: 11, padding: "3px 8px", borderRadius: 6, pointerEvents: "none",
        }}>
          Drag to explore
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ScoreBar
// ─────────────────────────────────────────────────────────────

function ScoreBar({ teams }) {
  return (
    <div style={{ display: "flex", gap: 10 }}>
      {Object.entries(TEAMS).map(([tid, cfg]) => {
        const t = teams[tid] || {};
        return (
          <div key={tid} style={{
            flex: 1, background: cfg.bg, border: `1.5px solid ${cfg.border}`,
            borderRadius: 10, padding: "10px 14px",
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <div>
              <div style={{ fontWeight: 500, fontSize: 14, color: cfg.color }}>{cfg.label}</div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}>
                {t.player_count ?? 0} player{t.player_count !== 1 ? "s" : ""}
              </div>
            </div>
            <div style={{ fontSize: 26, fontWeight: 500, color: cfg.color }}>{t.score ?? 0}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Lobby
// ─────────────────────────────────────────────────────────────

function Lobby({ state, myTeam, onJoin, onLeave }) {
  return (
    <div style={{ maxWidth: 480, margin: "0 auto", padding: "2.5rem 1rem" }}>
      <h1 style={{ fontSize: 24, fontWeight: 500, marginBottom: 4 }}>Transatlantic Trade Quiz</h1>
      <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 28 }}>
        {state.total_questions} questions · map &amp; multiple choice
      </p>

      <ScoreBar teams={state.teams} />

      <div style={{ marginTop: 28 }}>
        {!myTeam ? (
          <>
            <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 10 }}>Choose your team</p>
            <div style={{ display: "flex", gap: 12 }}>
              {Object.entries(TEAMS).map(([tid, cfg]) => {
                const ts   = state.teams[tid] || {};
                const full = (ts.player_count ?? 0) >= 8;
                return (
                  <button key={tid} onClick={() => !full && onJoin(tid)} disabled={full} style={{
                    flex: 1, padding: "20px 12px", borderRadius: 12,
                    border: `2px solid ${cfg.border}`,
                    background: full ? "#f3f4f6" : cfg.bg,
                    color: full ? "#9ca3af" : cfg.color,
                    fontWeight: 500, fontSize: 16,
                    cursor: full ? "not-allowed" : "pointer",
                  }}>
                    {cfg.label}
                    {full && <span style={{ display: "block", fontSize: 11, fontWeight: 400, marginTop: 4 }}>Full</span>}
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <div style={{
            background: TEAMS[myTeam].bg,
            border: `1.5px solid ${TEAMS[myTeam].border}`,
            borderRadius: 10, padding: "16px",
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <div style={{ fontSize: 14, color: TEAMS[myTeam].color }}>
              You're on <strong>{TEAMS[myTeam].label}</strong>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>
                Waiting for the host to start…
              </div>
            </div>
            <button onClick={onLeave} style={{
              padding: "6px 14px", borderRadius: 8,
              border: `1px solid ${TEAMS[myTeam].border}`,
              background: "transparent", color: TEAMS[myTeam].color,
              fontSize: 12, cursor: "pointer",
            }}>
              Leave
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Question
// ─────────────────────────────────────────────────────────────

function Question({ state, myTeam, myAnswer, revealData, onAnswer }) {
  const q = state.current_question;
  if (!q) return null;

  const isRevealed = state.phase === "reveal";
  const isMapQ     = q.type === "map";
  const cfg        = myTeam ? TEAMS[myTeam] : null;

  const getVotes = (tid, i) => revealData?.votes?.[tid]?.[i] ?? 0;
  const getTotal = (tid)    => revealData?.totals?.[tid]      ?? 0;

  const optionStyle = (i) => {
    const isCorrect  = isRevealed && i === revealData?.correct_index;
    const isMyWrong  = isRevealed && myAnswer === i && i !== revealData?.correct_index;
    const isSelected = !isRevealed && myAnswer === i;
    let bg = "#fff", border = "#e5e7eb", color = "#111827";
    if (isSelected) { bg = cfg?.light ?? "#e0f2fe"; border = cfg?.border ?? "#38bdf8"; }
    if (isCorrect)  { bg = "#f0fdf4"; border = "#4ade80"; color = "#166534"; }
    if (isMyWrong)  { bg = "#fef2f2"; border = "#f87171"; color = "#991b1b"; }
    return { bg, border, color };
  };

  const canAnswer = !!myTeam && !isRevealed && myAnswer === null;

  return (
    <>
        <div>
            {isMapQ && (
                <div style={{ height: "60vh", marginBottom: 14, overflow: "hidden" }}>
                <QuizMap
                    viewport={q.viewport}
                    marker={q.marker}
                    markerLabel={revealData?.marker_label}
                    revealed={isRevealed}
                />
                </div>
            )}
        </div>

        <div style={{ maxWidth: 680, margin: "0 auto", padding: "1.5rem 1rem" }}>

        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#9ca3af", marginBottom: 10 }}>
            <span>Question {state.question_index + 1} / {state.total_questions}</span>
            {isMapQ && <span style={{ background: "#f3f4f6", padding: "2px 8px", borderRadius: 4, color: "#6b7280" }}>Map question</span>}
        </div>

        <div style={{ marginBottom: 16 }}><ScoreBar teams={state.teams} /></div>

        <div style={{
            background: "#1e293b", color: "#f1f5f9", borderRadius: 12,
            padding: "14px 18px", fontSize: 16, fontWeight: 500,
            lineHeight: 1.5, marginBottom: 14,
        }}>
            {q.text}
        </div>

        <div>
            {q.options.map((opt, i) => {
            const { bg, border, color } = optionStyle(i);
            return (
                <div key={i}>
                <button
                    onClick={() => canAnswer && onAnswer(i)}
                    style={{
                    display: "flex", alignItems: "center", width: "100%",
                    textAlign: "left", padding: "10px 14px",
                    borderRadius: isRevealed ? "10px 10px 0 0" : 10,
                    border: `1.5px solid ${border}`, background: bg, color,
                    cursor: canAnswer ? "pointer" : "default",
                    fontSize: 14, marginBottom: isRevealed ? 0 : 8,
                    transition: "background 0.15s",
                    }}
                >
                    <span style={{
                    width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                    background: isRevealed && i === revealData?.correct_index ? "#4ade80"
                        : isRevealed && myAnswer === i ? "#f87171" : "#f3f4f6",
                    color: isRevealed && i === revealData?.correct_index ? "#166534"
                        : isRevealed && myAnswer === i ? "#991b1b" : "#374151",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 500, marginRight: 10,
                    }}>
                    {OPTION_LABELS[i]}
                    </span>
                    <span style={{ flex: 1 }}>{opt}</span>
                    {isRevealed && i === revealData?.correct_index && (
                    <span style={{ fontSize: 11, color: "#166534", marginLeft: 8 }}>✓ correct</span>
                    )}
                </button>

                {isRevealed && (
                    <div style={{
                    padding: "6px 14px 10px", background: bg,
                    border: `1.5px solid ${border}`, borderTop: "none",
                    borderRadius: "0 0 10px 10px", marginBottom: 8, display: "flex", gap: 8,
                    }}>
                    {Object.entries(TEAMS).map(([tid, tcfg]) => {
                        const v   = getVotes(tid, i);
                        const tot = getTotal(tid);
                        const pct = tot > 0 ? Math.round((v / tot) * 100) : 0;
                        return (
                        <div key={tid} style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, color: tcfg.color, marginBottom: 3 }}>
                            {tcfg.label}: {v}/{tot}
                            </div>
                            <div style={{ background: "#e5e7eb", borderRadius: 4, height: 5 }}>
                            <div style={{
                                width: `${pct}%`, height: 5, background: tcfg.border,
                                borderRadius: 4, transition: "width 0.5s",
                            }} />
                            </div>
                        </div>
                        );
                    })}
                    </div>
                )}
                </div>
            );
            })}
        </div>

        {isRevealed && revealData?.marker_label && (
            <div style={{
            background: "#f8fafc", border: "1px solid #e2e8f0",
            borderRadius: 8, padding: "8px 14px", marginBottom: 10,
            fontSize: 13, color: "#475569", display: "flex", alignItems: "center", gap: 8,
            }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#dc2626", display: "inline-block", flexShrink: 0 }} />
            <strong>{revealData.marker_label}</strong>
            </div>
        )}

        {!myTeam && (
            <p style={{ textAlign: "center", fontSize: 13, color: "#9ca3af", marginTop: 8 }}>
            You're watching — go back to the lobby to join a team.
            </p>
        )}
        {myTeam && !isRevealed && myAnswer === null && (
            <p style={{ textAlign: "center", fontSize: 13, color: "#6b7280", marginTop: 8 }}>
            Tap an answer above.
            </p>
        )}
        {myTeam && !isRevealed && myAnswer !== null && (
            <p style={{ textAlign: "center", fontSize: 13, color: "#9ca3af", marginTop: 8 }}>
            Answer locked in — waiting for the host to reveal…
            </p>
        )}
        {isRevealed && (
            <p style={{ textAlign: "center", fontSize: 13, color: "#9ca3af", marginTop: 8 }}>
            Waiting for the host to move on…
            </p>
        )}
        </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Finished
// ─────────────────────────────────────────────────────────────

function Finished({ state }) {
  const entries    = Object.entries(state.teams).sort((a, b) => b[1].score - a[1].score);
  const tied       = entries[0][1].score === entries[1][1].score;
  const [winnerId] = entries[0];
  const cfg        = TEAMS[winnerId];

  return (
    <div style={{ maxWidth: 440, margin: "4rem auto", textAlign: "center", padding: "0 1rem" }}>
      <div style={{ fontSize: 52, marginBottom: 10 }}>🏆</div>
      <h2 style={{ fontSize: 22, fontWeight: 500, marginBottom: 4 }}>
        {tied ? "It's a tie!" : `${cfg.label} wins!`}
      </h2>
      <p style={{ color: "#9ca3af", fontSize: 14, marginBottom: 32 }}>Final scores</p>
      <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
        {entries.map(([tid, t]) => {
          const c        = TEAMS[tid];
          const isWinner = !tied && tid === winnerId;
          return (
            <div key={tid} style={{
              flex: 1, background: c.bg,
              border: `${isWinner ? 2 : 1.5}px solid ${c.border}`,
              borderRadius: 14, padding: "20px 16px",
            }}>
              {isWinner && (
                <div style={{
                  fontSize: 11, fontWeight: 500, background: c.light, color: c.color,
                  borderRadius: 6, padding: "2px 8px", display: "inline-block", marginBottom: 8,
                }}>Winner</div>
              )}
              <div style={{ fontSize: 14, fontWeight: 500, color: c.color, marginBottom: 4 }}>{c.label}</div>
              <div style={{ fontSize: 38, fontWeight: 500, color: c.color }}>{t.score}</div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>correct answers</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Root
// ─────────────────────────────────────────────────────────────

export default function Quiz() {
  const socketRef = useRef(null);
  const [connected, setConnected]   = useState(false);
  const [error, setError]           = useState(null);
  const [gameState, setGameState]   = useState(null);
  const [myTeam, setMyTeam]         = useState(() => localStorage.getItem("quiz_team") || null);
  const [myAnswer, setMyAnswer]     = useState(() => {
    const s = sessionStorage.getItem("quiz_answer");
    return s !== null ? parseInt(s, 10) : null;
  });
  const [revealData, setRevealData] = useState(null);

  useEffect(() => {
    const socket = io(SOCKET_URL, { transports: ["websocket", "polling"] });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      const savedTeam = localStorage.getItem("quiz_team");
      if (savedTeam) socket.emit("join_team", { team: savedTeam });
    });

    socket.on("disconnect", () => setConnected(false));
    socket.on("error",      (d) => setError(d.msg));

    socket.on("joined", (d) => {
      setMyTeam(d.team);
      localStorage.setItem("quiz_team", d.team);
    });

    socket.on("left_team", () => {
      setMyTeam(null);
      localStorage.removeItem("quiz_team");
    });

    socket.on("game_reset", () => {
      localStorage.removeItem("quiz_team");
      sessionStorage.removeItem("quiz_answer");
      setMyTeam(null);
      setMyAnswer(null);
      setRevealData(null);
    });

    socket.on("state", (s) => {
      setGameState(s);
      if (s.phase === "question") {
        sessionStorage.removeItem("quiz_answer");
        setMyAnswer(null);
        setRevealData(null);
      }
    });

    socket.on("answer_ack", (d) => {
      sessionStorage.setItem("quiz_answer", String(d.option_index));
      setMyAnswer(d.option_index);
    });

    socket.on("reveal_data", (d) => setRevealData(d));

    return () => socket.disconnect();
  }, []);

  const emit = useCallback((ev, data) => socketRef.current?.emit(ev, data), []);

  const handleJoin  = useCallback((tid) => emit("join_team", { team: tid }), [emit]);
  const handleLeave = useCallback(() => {
    localStorage.removeItem("quiz_team");
    sessionStorage.removeItem("quiz_answer");
    setMyAnswer(null);
    emit("leave_team");
  }, [emit]);

  if (!connected || !gameState) {
    return (
      <div style={{ textAlign: "center", padding: "4rem 1rem", color: "#9ca3af", fontSize: 14 }}>
        {connected ? "Loading…" : "Connecting to server…"}
      </div>
    );
  }

  return (
    <div style={{ background: "#f9fafb", minHeight: "100vh" }}>

      {error && (
        <div style={{
          background: "#fef2f2", color: "#991b1b", padding: "8px 20px",
          fontSize: 13, borderBottom: "1px solid #fca5a5",
          display: "flex", justifyContent: "space-between",
        }}>
          {error}
          <button onClick={() => setError(null)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#991b1b" }}>×</button>
        </div>
      )}

      {gameState.phase === "lobby" && (
        <Lobby state={gameState} myTeam={myTeam} onJoin={handleJoin} onLeave={handleLeave} />
      )}

      {(gameState.phase === "question" || gameState.phase === "reveal") && (
        <Question
          state={gameState}
          myTeam={myTeam}
          myAnswer={myAnswer}
          revealData={revealData}
          onAnswer={(i) => emit("submit_answer", { option_index: i })}
        />
      )}

      {gameState.phase === "finished" && <Finished state={gameState} />}
    </div>
  );
}